import { initializeApp, getApps, getApp } from "firebase/app";
import { getMessaging, getToken, isSupported, type Messaging } from "firebase/messaging";

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

function getFirebaseApp() {
  return getApps().length ? getApp() : initializeApp(firebaseConfig);
}

let messagingPromise: Promise<Messaging | null> | null = null;

function getMessagingInstance(): Promise<Messaging | null> {
  if (!messagingPromise) {
    messagingPromise = isSupported().then((ok) => (ok ? getMessaging(getFirebaseApp()) : null));
  }
  return messagingPromise;
}

let cachedFcmToken = "";

/** Sync read for axios interceptors — "" until {@link requestFcmToken} resolves. */
export function getCachedFcmToken(): string {
  return cachedFcmToken;
}

/** Set by the native push path (see lib/native-bridge.ts) when running inside the Capacitor app. */
export function setCachedFcmToken(token: string): void {
  cachedFcmToken = token;
}

/**
 * Shows the browser's native notification permission prompt (skipped
 * automatically by the browser if already granted/denied from a previous
 * visit) and fetches the FCM token if granted. Never throws — "" means
 * unsupported, denied, or SW/token failure.
 */
export async function requestFcmToken(): Promise<string> {
  if (typeof window === "undefined" || !("Notification" in window)) return "";
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return "";
  cachedFcmToken = await getFcmToken();
  return cachedFcmToken;
}

async function getFcmToken(): Promise<string> {
  try {
    const messaging = await getMessagingInstance();
    if (!messaging) return "";

    const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    const token = await getToken(messaging, {
      vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    return token ?? "";
  } catch {
    return "";
  }
}
